import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle, Package, Wrench, XCircle } from 'lucide-react';

interface ComebackResolutionModalProps {
  isOpen: boolean;
  onResolve: (resolution: 'parts_failure' | 'tech_fault' | 'unrelated') => Promise<void>;
  ticketId: string;
}

export const ComebackResolutionModal = ({ 
  isOpen, 
  onResolve,
  ticketId
}: ComebackResolutionModalProps) => {
  const [selectedResolution, setSelectedResolution] = useState<'parts_failure' | 'tech_fault' | 'unrelated' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedResolution) return;
    
    setIsSubmitting(true);
    try {
      await onResolve(selectedResolution);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            How was this comeback resolved?
          </DialogTitle>
          <DialogDescription>
            This information helps us improve technician assignments and track warranty issues.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={selectedResolution || ''} onValueChange={(value) => setSelectedResolution(value as any)}>
          <div className="space-y-3">
            <div className="flex items-start space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer transition-colors">
              <RadioGroupItem value="parts_failure" id="parts_failure" className="mt-1" />
              <Label htmlFor="parts_failure" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-semibold">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  Parts Failure
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  The issue was caused by a defective or failed part
                </p>
              </Label>
            </div>

            <div className="flex items-start space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer transition-colors">
              <RadioGroupItem value="tech_fault" id="tech_fault" className="mt-1" />
              <Label htmlFor="tech_fault" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-semibold">
                  <Wrench className="h-4 w-4 text-destructive" />
                  Technician Fault
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  The original repair was incomplete or incorrect
                </p>
              </Label>
            </div>

            <div className="flex items-start space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer transition-colors">
              <RadioGroupItem value="unrelated" id="unrelated" className="mt-1" />
              <Label htmlFor="unrelated" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-semibold">
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                  Unrelated to Prior Repair
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  This is a new, unrelated issue with the vehicle
                </p>
              </Label>
            </div>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedResolution || isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Saving...' : 'Save Resolution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
